import { Button } from '@/components';
import { datadogRum } from '@datadog/browser-rum';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import {
  getKsAnalysisSearchHistory,
  pickKsDrillDownState,
  removeKsAnalysisSearchHistory,
  setKsAnalysisSearchHistory,
} from '../_utils/ks-analysis-session-storage';

type Props = {
  disabled: boolean;
};

/**
 * 基金シート分析画面のドリルダウン履歴を 1 ステップ戻すボタン。
 * RS 用 `FilterHistoryBackButton` の KS 版（state 形が異なるため別実装）。
 *
 * 履歴に積む・復元するのは**ドリルダウン state（cascader / organizationIds）のみ** (RS-4493)。
 * URL クエリ全量を snapshot すると、年度タブ(chartYear)・列ソート(order)・ページング(page)の
 * 変更まで履歴に乗り、「戻る」がドリルダウンではなくそれらを巻き戻してしまう。
 */
export const KsFilterHistoryBackButton: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useFundSheetAnalysisPageSearchQueryStates();

  useEffect(() => {
    const drillDownState = pickKsDrillDownState(searchQueryStates);
    if (disabled) {
      removeKsAnalysisSearchHistory();
      setKsAnalysisSearchHistory([drillDownState]);
      return;
    }
    // getKsAnalysisSearchHistory が entry をドリルダウン state に正規化して返すため、
    // 旧形式（全量 snapshot）が残っていても比較は壊れない
    const history = getKsAnalysisSearchHistory();
    const lastState = history.at(-1);
    const isNewState =
      !lastState ||
      JSON.stringify(lastState) !== JSON.stringify(drillDownState);
    if (isNewState) {
      setKsAnalysisSearchHistory([...history, drillDownState]);
    }
  }, [searchQueryStates, disabled]);

  const applyAndPopFilter = useCallback(() => {
    const history = getKsAnalysisSearchHistory();
    datadogRum.addAction('基金シート分析: 履歴を戻るボタンが使用されました。');
    // 現在と同じドリルダウン state の entry を末尾からスキップし、
    // 最初に見つかった異なる state を復元先にする。旧形式（全量 snapshot）の履歴では
    // 年度タブ・ソート変更で同一ドリルダウンの entry が連続し得るため、
    // 単純な at(-2) だと「戻る」がドリルダウンを戻せないことがある (RS-4493)。
    const currentKey = JSON.stringify(pickKsDrillDownState(searchQueryStates));
    let targetIndex = history.length - 1;
    while (
      targetIndex >= 0 &&
      JSON.stringify(history[targetIndex]) === currentKey
    ) {
      targetIndex -= 1;
    }
    const targetState = history[targetIndex];
    if (!targetState) {
      return;
    }
    // ドリルダウン state のみ復元し、年度タブ・ソート・グラフ種別等の現在値は維持する (RS-4493)。
    // ドリルダウンが戻ると結果集合が変わるため page は 1 に戻す。
    setSearchQueryStates((prev) => ({
      ...prev,
      ...targetState,
      page: 1,
    }));
    setKsAnalysisSearchHistory(history.slice(0, targetIndex + 1));
  }, [setSearchQueryStates, searchQueryStates]);

  return (
    <Button onClick={applyAndPopFilter} disabled={disabled}>
      {t('filter-history-back')}
    </Button>
  );
};
